import { useCallback, useContext, useState } from 'react';
import { useAccount } from 'wagmi';

import { ApiPairs } from 'api';
import { DepositWithdrawContext } from 'context/DepositWithdrawContext/DepositWithdrawContext';
import { useERC20Deposit } from 'hooks/dex';
import { useTypedDispatch } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { bn } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { Button } from 'ui/Button/Button';

interface IUseDepositProps {
  onClose: () => void;
  pair: IDexPair;
}

const useDeposit = ({ onClose, pair }: IUseDepositProps) => {
  const { address } = useAccount();
  const dispatch = useTypedDispatch();
  const { innerWalletsValid, innerWallets, formError, selectedToken } =
    useContext(DepositWithdrawContext);

  const { deposit } = useERC20Deposit(selectedToken.get.address);

  const [loading, setLoading] = useState(false);

  const handleDeposit = useCallback(async () => {
    const transactionHashes: string[] = [];

    if (!address) {
      return onClose();
    }

    if (formError || !innerWalletsValid) return;

    try {
      setLoading(true);

      const depositWallets = innerWallets.get.filter(el => !el.disabled);

      for (const wallet of depositWallets) {
        const result = await deposit({
          walletAddress: wallet.address,
          amount: bn(wallet.balance, selectedToken.get.decimals),
        });

        if (typeof result === 'string') {
          transactionHashes.push(result);
        } else if (result && 'hash' in (result as any)) {
          const _result = result as any;

          if (_result.hash && typeof _result.hash === 'string') {
            transactionHashes.push(_result.hash);
          }
        }
      }
    } catch (error) {
      console.log(error);
      return onClose();
    } finally {
      setLoading(false);
    }

    if (transactionHashes.length === 0) {
      onClose();
      dispatch(
        setAlertState({
          type: 'failed',
          text: 'Something went wrong with signing transactions in metamask',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => dispatch(dropAlertState()),
          buttons: (
            <div className="w-full flex flex-col items-center">
              <Button color={'error'} onClick={() => dispatch(dropAlertState())}>
                Okay
              </Button>
            </div>
          ),
        }),
      );
      return;
    }

    try {
      setLoading(true);
      const { errorMessage, isSuccess } = await ApiPairs.deposit({
        pairId: pair.id,
        transactionHashes,
      });

      onClose();
      if (isSuccess) {
        dispatch(
          setAlertState({
            type: 'success',
            text: 'Transactions in process. Deposit is handling',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      } else {
        dispatch(
          setAlertState({
            type: 'failed',
            text: errorMessage,
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [
    innerWalletsValid,
    formError,
    innerWallets.get,
    deposit,
    selectedToken,
    pair,
    address,
    dispatch,
    onClose,
  ]);

  return { deposit: handleDeposit, loading };
};

export default useDeposit;
